import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { RouterContext, goTo, useOnUnload } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem, getKeyHandler, useConfirmationModal } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton, Note, useToast, Input } from 't-lib-ui/widgets';
import { chevronBackIcon, trashIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, useSetStuff, getAutocomplete } from '../../connector/api';
import { QueryStatus } from 't-lib-ui/connector';
import { nonNull } from 't-lib-ui/helpers';
import { debounce1 } from 't-lib-ui/helpers';

interface AutoAddFeedsConfig {
  feeds: string[];
}

interface FeedInfo {
  id: string;
  title: string;
  domain: string;
}

const AdminAutoFeedsPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [, setSaving] = useState(false);
  const [feedIds, setFeedIds] = useState<string[]>([]);
  const [feedInfoMap, setFeedInfoMap] = useState<Map<string, FeedInfo>>(new Map());
  const [originalFeedIds, setOriginalFeedIds] = useState<string[]>([]);
  const [showToast, toastProps] = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FeedInfo[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminAutoFeedsPage', {
      'Escape': () => {
        if (showSearchResults) {
          setShowSearchResults(false);
          return true;
        }
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);
    return () => unregisterKeyHandler('adminAutoFeedsPage');
  }, [routerCtx, showSearchResults]);

  const { data: config, status: loadStatus } = useGetStuff<AutoAddFeedsConfig>('auto-add-feeds', magic);
  const setStuff = useSetStuff<AutoAddFeedsConfig>('auto-add-feeds', magic);

  useEffect(() => {
    if (!config?.feeds) return;
    setFeedIds(config.feeds);
    setOriginalFeedIds(config.feeds);

    const auth = appState.state.authData;
    if (auth) {
      for (const feedId of config.feeds) {
        getAutocomplete(auth, feedId).then(results => {
          const match = results.find(([id]) => id == feedId);
          if (match)
            setFeedInfoMap(prev => new Map(prev).set(feedId, { id: match[0], title: match[1], domain: match[2] }));
        }).catch(() => {});
      }
    }
  }, [config, appState.state.authData]);

  const debouncedSearch = useMemo(
    () => debounce1(1, async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedIndex(-1);
        return;
      }
      setSearchLoading(true);
      try {
        const auth = nonNull(appState.state.authData);
        const results = await getAutocomplete(auth, query);
        const mapped = results.map(([id, title, domain]) => ({ id, title, domain }));
        setSearchResults(mapped);
        setShowSearchResults(true);
        setSelectedIndex(mapped.length == 1 ? 0 : -1);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400),
    [appState.state.authData]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddFeed = useCallback((feedInfo: FeedInfo) => {
    if (feedIds.includes(feedInfo.id)) return;
    setFeedIds([...feedIds, feedInfo.id]);
    setFeedInfoMap(new Map(feedInfoMap).set(feedInfo.id, feedInfo));
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSelectedIndex(-1);
  }, [feedIds, feedInfoMap]);

  const handleInputKeyDown = useMemo(() => {
    const flowHandler = getKeyHandler({
      enterBehaviour: 'next',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'leftAtBeginning'
    });

    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSearchResults && searchResults.length) {
        if (e.key == 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
          return;
        } else if (e.key == 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          return;
        } else if (e.key == 'Enter') {
          e.preventDefault();
          if (searchResults.length == 1)
            handleAddFeed(searchResults[0]);
          else if (selectedIndex >= 0 && selectedIndex < searchResults.length)
            handleAddFeed(searchResults[selectedIndex]);
          return;
        } else if (e.key == 'Escape') {
          setShowSearchResults(false);
          return;
        }
      }
      flowHandler(e as any);
    };
  }, [showSearchResults, searchResults, selectedIndex, handleAddFeed]);

  const handleDeleteFeed = (feedId: string) => {
    setFeedIds(feedIds.filter(id => id != feedId));
  };

  const hasChanges = JSON.stringify(feedIds) != JSON.stringify(originalFeedIds);

  const leave = () => goTo(routerCtx, '/a/admin/feeds');

  const leaveConfirmation = useConfirmationModal({
    title: 'Discard Changes?',
    children: 'You have unsaved changes. Do you really want to leave without saving?',
    confirmContent: 'Discard Changes',
    dismissContent: 'Keep Editing',
    onConfirm: leave
  });

  const handleBack = () => {
    if (hasChanges) leaveConfirmation();
    else leave();
  };

  useOnUnload(() => !hasChanges, hasChanges);

  // Auto-save
  useEffect(() => {
    if (!hasChanges || loadStatus == QueryStatus.Loading || loadStatus == QueryStatus.Pending) return;

    const timeoutId = setTimeout(async () => {
      setSaving(true);
      showToast({ children: 'Saving...', type: 'info' }, 10000);
      try {
        await setStuff({ feeds: feedIds });
        setOriginalFeedIds(feedIds);
        showToast({ children: 'Saved', type: 'success' }, 2000);
      } catch (error) {
        console.error('Auto-save error:', error);
        showToast({ children: 'Error saving changes', type: 'error' }, 3000);
      } finally {
        setSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [feedIds, originalFeedIds, loadStatus, setStuff, showToast]);

  const loading = loadStatus == QueryStatus.Loading;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-auto-feeds"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <AdminHeader title="Auto-Add Feeds" onExpand={onExpand} />
                <p style={{ marginBottom: '24px', color: 'var(--gray-400)' }}>
                  {loading ? 'Loading...' : `${feedIds.length} source feeds configured. Posts from these feeds are scanned every 2 hours to discover new blog URLs and automatically add them.`}
                </p>

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={handleBack}
                    focussable="flow"
                  />
                </ButtonBar>

                <Note {...toastProps} />

                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <h3 style={{ marginBottom: '12px' }}>Search and Add Source Feed</h3>
                  <div style={{ position: 'relative', maxWidth: '500px' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowSearchResults(false);
                          setSelectedIndex(-1);
                        }, 200);
                      }}
                      placeholder="Search feeds by name..."
                      className="flowFocussable noClickFocus"
                      tabIndex={0}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--gray-800)',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}
                    />
                    {searchLoading && (
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--gray-400)',
                        fontSize: '0.85rem'
                      }}>
                        Loading...
                      </div>
                    )}
                    {showSearchResults && searchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--surface-raised)',
                        border: '1px solid var(--gray-800)',
                        borderRadius: '4px',
                        marginTop: '4px',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                      }}>
                        {searchResults.map((result, index) => {
                          const isAlreadyAdded = feedIds.includes(result.id);
                          const isSelected = index == selectedIndex;
                          return (
                            <div
                              key={result.id}
                              onClick={() => handleAddFeed(result)}
                              onMouseEnter={() => setSelectedIndex(index)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: index < searchResults.length - 1 ? '1px solid var(--gray-950)' : 'none',
                                backgroundColor: isAlreadyAdded ? 'var(--gray-975)' : (isSelected ? 'var(--gray-925)' : 'var(--surface-raised)')
                              }}
                            >
                              <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {result.title}
                                {isAlreadyAdded && (
                                  <span style={{ color: 'var(--success-color)', marginLeft: '8px', fontSize: '0.85rem' }}>Added</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.8em', color: 'var(--gray-400)', marginTop: '2px' }}>
                                {result.domain}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <h3 style={{ marginBottom: '12px' }}>Source Feeds</h3>
                <List>
                  {feedIds.length == 0 && !loading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                      No source feeds configured yet. Search and add some above!
                    </div>
                  )}
                  {feedIds.map((feedId) => {
                    const feedInfo = feedInfoMap.get(feedId);
                    return (
                      <ListItem key={feedId} focussable="normal" href={`/a/admin/feed/${feedId}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{feedInfo?.title || feedId}</div>
                            <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
                              {feedInfo?.domain || ''} {feedInfo ? `\u2022 ${feedId}` : ''}
                            </div>
                          </div>
                          <TextButton
                            label="Delete"
                            icon={trashIcon}
                            onClick={() => handleDeleteFeed(feedId)}
                            focussable="flow"
                            size="small"
                            type="minor"
                          />
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminAutoFeedsPage;
